import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Filter } from '../model/table.model';
import { ButtonComponent } from '../../button/button.component';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import { InputComponent } from '../../input/input.component';
import { CalendarComponent } from '../../calendar/calendar.component';
import { NgIf, NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.css',
  imports: [ButtonComponent, DropdownComponent, InputComponent, CalendarComponent, NgTemplateOutlet, NgIf],
  standalone: true,
})
export class FilterComponent {
  /** Muestra/oculta el botón de "Limpiar filtros" (por defecto visible) */
  @Input() showClearButton: boolean = true;
  async clearFilters() {
    this.filtros.forEach((filtro) => {
      if (filtro.value) {
        filtro.value = undefined;
        // this.changeInput(filtro.query, '');
        this.event.emit({ key: '', value: '', reset: true });
      }
    });

    const params = await firstValueFrom(this.route.queryParams);
    Object.keys(params).forEach((key) => {});

    this.router.navigate([], {
      queryParams: {}, // 🔹 Elimina todos los query params
      replaceUrl: true,
    });
  }
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  async ngOnInit() {
    const params = await firstValueFrom(this.route.queryParams);

    Object.keys(params).forEach((key) => {
      const filter = this.filtros.find((filtro) => filtro.query === key);
      if (filter) {
        let value: any;
        try {
          value = JSON.parse(params[key]);
        } catch (error) {
          value = params[key];
        }

        // Caso especial: filtros de fecha
        if (filter.type === 'date') {
          const parsedDate = this.parseDateForControl(value);
          filter.value = parsedDate ?? undefined;
          this.changeInput(key, parsedDate ?? '');
          return;
        }

        // Resto de filtros (dropdown / input / multiselect)
        const strOrArr = Array.isArray(value) ? value : value.toString();
        filter.value = filter.options
          ? filter.type === 'multiselect'
            ? filter.options.filter((op) =>
                filter.searchProperty
                  ? strOrArr.includes(op[filter.searchProperty!])
                  : op == strOrArr
              )
            : filter.options.find((op) =>
                filter.searchProperty
                  ? op[filter.searchProperty!] == strOrArr
                  : op == strOrArr
              )
          : strOrArr;
        this.changeInput(key, strOrArr);
      }
    });
  }

  @Input() filtros: Filter[] = [];

  @Output('changeInput') private event = new EventEmitter<{
    key: string;
    value: string | string[];
    reset?: boolean;
  }>();

  async changeInput(key: string, value: any) {
    const normalized = this.normalizeValueForQuery(value);
    const normalizedArray = Array.isArray(value)
      ? value
          .map((v) => this.normalizeValueForQuery(v))
          .filter((v) => v !== '')
      : [];

    const shouldDelete = Array.isArray(value)
      ? normalizedArray.length === 0
      : normalized === '' || normalized === undefined;

    this.event.emit({
      key: key,
      value: Array.isArray(value) ? normalizedArray : normalized,
    });
    const params = await firstValueFrom(this.route.queryParams);
    const newParams = { ...params }; // Copia los parámetros actuales

    if (shouldDelete) {
      delete newParams[key]; // 🔹 Elimina el parámetro si está vacío
    } else {
      newParams[key] = Array.isArray(value)
        ? normalizedArray
        : normalized; // 🔹 Agrega o actualiza el parámetro
    }

    this.router.navigate([], {
      queryParams: newParams,
      replaceUrl: true, // 🔹 No afecta el historial
    });
  }

  ngOnDestroy() {
    this.router.navigate([], {
      queryParams: {}, // 🔹 Elimina todos los query params
      queryParamsHandling: 'merge', // 🔹 Evita que los anteriores se mantengan
      replaceUrl: true,
    });
  }

  returnMap(options: any, searchProperty: string | undefined) {
    if (!searchProperty) return;
    return options.map((option: any) => option[searchProperty]);
  }

  // ===== Helpers para fecha =====
  // Convierte Date a ISO UTC a medianoche (YYYY-MM-DDT00:00:00.000Z)
  private dateToIsoUtcMidnight(d: Date): string {
    const y = d.getFullYear();
    const m = d.getMonth();
    const day = d.getDate();
    return new Date(Date.UTC(y, m, day, 0, 0, 0, 0)).toISOString();
  }

  // Normaliza cualquier valor a string para query/emit
  private normalizeValueForQuery(value: any): string {
    if (value === undefined || value === null) return '';
    if (value instanceof Date) return this.dateToIsoUtcMidnight(value);
    if (typeof value === 'object' && !Array.isArray(value)) return JSON.stringify(value);
    return String(value);
  }

  // Convierte string ISO o YYYY-MM-DD a Date local a medianoche
  private parseDateForControl(value: any): Date | null {
    if (value instanceof Date) return value;
    if (typeof value !== 'string' || !value) return null;
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const y = +m[1];
      const mon = +m[2] - 1;
      const d = +m[3];
      return new Date(y, mon, d, 0, 0, 0, 0);
    }
    const dt = new Date(value);
    return isNaN(dt.getTime()) ? null : dt;
  }
}
